import { Button, Drawer, Dropdown, Input, Modal, Radio, Switch, Tooltip, message } from "antd";
import "./CreatePlaylistModal.scss";
import React, { ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  ClockCircleOutlined, CloseOutlined, DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  LeftOutlined,
  LoadingOutlined,
  MenuOutlined,
  QuestionCircleOutlined,
  SearchOutlined
} from "@ant-design/icons";
import LocationsApi from "../../api/LocationsApi";
import { getLocationDesignsPerPage, NO_ASSET_LABEL } from "../../service/DsaService";
import DSAApi from "../../api/DSAApi";
import { DsaPlaylistDto, DsaPlaylistEntryDto, DsaPlaylistEntryDtoTypeEnum, DsaScreenDto } from "../../generated/dashboard-client";
import { handleServerError } from "../../util/ErrorHandler";
import { DesignView, SearchableUpload } from "./EditScreenContentModal";
import TransitionIcon from "../icons/TransitionIcon";
import SortableList, { SortableItem, SortableKnob } from 'react-easy-sort';
import useFocus from "../../hooks/useFocus";
import { DesignSizes } from "../../util/constants";
import PaintbrushIcon from "../icons/content-pages/PaintbrushIcon";
import { NavLink } from "react-router-dom";
import { env } from "../../env/env";
import { getRendererThumbPathWithPage, getS3Url } from "../../util/utils";
import MediaThumb from "./MediaThumb";
import { getDesignTimberLink } from "../../service/PosService";
import PushToScreenMenu from "./PushToScreenMenu";
import { useOnClickOutside } from "usehooks-ts";
import { Spinner } from "../account/misc/Spinner";
import { useRecoilValue } from "recoil";
import { mobileState } from "../../state/atoms";
import PlaylistIcon from "../icons/PlaylistIcon";
import { AppContext } from "../../AppContext";
import { DisplayUploadDto } from "../../domain/DisplayUploadDto";
import UploadDisplayImage from "./UploadDisplayImage";
import QRDisplayUploadsApi from "../../api/QRDisplayUploadsApi";
import UnpublishedChangesNote from "./UnpublishedChangesNote";
import { usePublishDesign } from "./usePublishDesign";

export interface CreatePlaylistModalProps {
  isVisible: boolean;
  onClose: (fetch: boolean) => void;
  locationId: number;
  currentPlaylist: DsaPlaylistDto | null;
  setCurrentPlaylist: (playlist: DsaPlaylistDto) => void;
  setPreviewEntries: (entries: DsaPlaylistEntryDto[]) => void;
  setPreviewModal: (visible: boolean) => void;
  pushPlaylistToScreens: (playlist: DsaPlaylistDto, screens: DsaScreenDto[], callback?: () => void) => void;
  pushLoading: boolean;
  screens: DsaScreenDto[];
}

enum ViewType {
  Name, Config
}

enum DesignUploadView {
  Designs, Uploads
}

interface PlaylistObjectView extends DesignView {
  duration?: number;
  transition?: string;
  sequence?: number;
  type?: DsaPlaylistEntryDtoTypeEnum;
  uploadId?: string;
  path?: string;
}

const editViews = {
  selectDesign: 'select-design',
  editPl: 'edit-pl'
}

const MIN_PLAYLIST_DURATION = 20;
const DEFAULT_PLAYLIST_DURATION = 120;

export function CreatePlaylistModal({ isVisible, onClose, locationId, currentPlaylist, setCurrentPlaylist, setPreviewEntries, setPreviewModal, pushPlaylistToScreens, pushLoading, screens }: CreatePlaylistModalProps) {
  const [isDesignsLoading, setDesignsLoading] = useState(false);
  const [view, setView] = useState<ViewType>(ViewType.Name);
  const [isRenaming, setRenaming] = useState(false);
  const [renameInputRef, setRenameInputFocus] = useFocus();
  const [name, setName] = useState("");
  const [search, setSearch] = useState("");
  const [designUploadView, setDesignUploadView] = useState(DesignUploadView.Designs);
  const [designs, setDesigns] = useState<PlaylistObjectView[]>([]);
  const [uploads, setUploads] = useState([] as SearchableUpload[]);
  const [landscape, setLandscape] = useState(true);
  const [entries, setEntries] = useState<PlaylistObjectView[]>([]);
  const [saving, setSaving] = useState(false);
  const [savedPl, setSavedPl] = useState({} as DsaPlaylistDto | undefined);
  const [isPushPlaylistDropdown, setPushPlaylistDropdown] = useState(false);
  const [uploading, setUploading] = useState(false);
  const menuRef = useRef(null);

  const mobile = useRecoilValue(mobileState);
  const { location } = useContext(AppContext);
  const [mobileEditView, setMobileEditView] = useState(editViews.editPl);
  const [mobileDesignSelected, setMobileDesignSelected] = useState({} as PlaylistObjectView);
  const [mobileUploadSelected, setMobileUploadSelected] = useState({} as SearchableUpload);
  const { publish } = usePublishDesign();

  function isLandscape(des: PlaylistObjectView) {
    if (des.size === "custom") {
      return parseFloat(des.width!) > parseFloat(des.height!);
    } else {
      const data = DesignSizes[des.size ?? ""];
      if (data) {
        let [width, height] = data[1].split(" x ");
        return parseFloat(width) > parseFloat(height);
      }
    }
    return false;
  }

  const filterBoards = useCallback((data: PlaylistObjectView[], search: string, landscape: boolean) => {
    data.forEach(it => {
      const designLandscape = isLandscape(it);
      const isSameSize = (landscape && designLandscape) || (!landscape && !designLandscape);
      it.visible = isSameSize && it.name.toLowerCase().indexOf(search.toLowerCase()) > -1;
    });
    setDesigns(data);
  }, []);

  useEffect(() => {
    setLandscape(true);

    function fillInEntries(designs: DesignView[], uploads: DisplayUploadDto[]) {
      const currentObjects = currentPlaylist!.entries!.map((it) => {
        const isUpload = it.type === DsaPlaylistEntryDtoTypeEnum.Upload;
        const currentDesign = designs.find(obj => (obj.id === it.designId && obj.page === it.page));
        const currentUpload = uploads.find(obj => obj.id === it.uploadId)!

        return {
          id: isUpload ? it.uploadId : it.designId,
          type: isUpload ? DsaPlaylistEntryDtoTypeEnum.Upload : DsaPlaylistEntryDtoTypeEnum.Design,
          page: it.page || 0,
          name: isUpload ? currentUpload.name : (currentDesign?.name ?? NO_ASSET_LABEL),
          thumbnail: isUpload ? getS3Url(currentUpload.path!) : getRendererThumbPathWithPage(it.designId!, it.page!),
          duration: it.durationSeconds,
          transition: it.transition,
          sequence: it.sequence,
          uploadId: isUpload ? it.uploadId : '',
          hasUnpublishedChanges: isUpload ? false : (currentDesign?.hasUnpublishedChanges ?? false),
        } as PlaylistObjectView;
      });

      setEntries(currentObjects.sort((a, b) => { return a.sequence! - b.sequence! }));
      setName(currentPlaylist!.name!);
    }

    if (currentPlaylist && currentPlaylist.id) {
      setView(ViewType.Config);
      const playlistLocation = currentPlaylist?.locationId ? currentPlaylist.locationId : locationId;
      LocationsApi.getLocationDesigns(playlistLocation).then(resp => {
        const designsPageExpanded = getLocationDesignsPerPage(resp.data);
        QRDisplayUploadsApi.getDisplayUploadsByLocationId(playlistLocation.toString()).then(resp2 => {
          fillInEntries(designsPageExpanded, resp2.data);
        }).catch(() => {
          fillInEntries(designsPageExpanded, []);
        })
      });
    } else {
      setEntries([]);
      setName("");
      setView(ViewType.Name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPlaylist])

  function searchDesignByName(key: string) {
    filterBoards(designs, key, landscape);
    if (!key) {
      uploads.forEach(u => u.visible = true);
    } else {
      uploads.forEach(u => {
        u.visible = u.name!.toLowerCase().indexOf(key.toLowerCase()) > -1;
      });
    }
    setUploads([...uploads]);
    setSearch(key);
  }

  function onDesignUploadViewClick(view: DesignUploadView) {
    searchDesignByName('');
    setDesignUploadView(view);
  }

  useEffect(() => {
    setPreviewEntries(entries.map(e => {
      return {
        sequence: e.sequence,
        designId: e.id,
        page: e.page,
        durationSeconds: e.duration,
        type: e.type,
        uploadId: e.uploadId
      } as DsaPlaylistEntryDto;
    }))
  }, [setPreviewEntries, entries]);

  useEffect(() => {
    if (!isVisible) return;

    if (locationId) {
      setDesignsLoading(true);
      const playlistLocation = currentPlaylist?.locationId ? currentPlaylist.locationId : locationId;
      LocationsApi.getLocationDesigns(playlistLocation).then(resp => {
        filterBoards(getLocationDesignsPerPage(resp.data), "", landscape);
        setDesignsLoading(false);
      });

      QRDisplayUploadsApi.getDisplayUploadsByLocationId(playlistLocation.toString()).then(resp => {
        const dus = resp.data as SearchableUpload[];
        dus.forEach(it => it.visible = true);
        setUploads(dus);
      });
    }
  }, [locationId, isVisible, filterBoards, landscape, currentPlaylist?.locationId]);

  function onDesignSelect(design: PlaylistObjectView) {
    if (!mobile) {
      addDesign(design);
    } else {
      setMobileDesignSelected(design);
    }
  }

  function onUploadSelect(upload: SearchableUpload) {
    if (!mobile) {
      addUpload(upload);
    } else {
      setMobileUploadSelected(upload);
    }
    return;
  }

  function onObjectSelectDoneClick() {
    if (mobile) {
      if (designUploadView === DesignUploadView.Designs) {
        onDesignSelectDoneClick();
      } else if (designUploadView === DesignUploadView.Uploads) {
        onUploadSelectDoneClick();
      }
      setMobileDesignSelected({} as PlaylistObjectView);
      setMobileUploadSelected({} as SearchableUpload);
    }
  }

  function onDesignSelectDoneClick() {
    if (mobile) {
      addDesign(mobileDesignSelected);
      setMobileEditView(editViews.editPl);
    }
  }

  function onUploadSelectDoneClick() {
    if (mobile) {
      addUpload(mobileUploadSelected);
      setMobileEditView(editViews.editPl);
    }
  }

  function mobileDoneButtonEnabled() {
    if (designUploadView === DesignUploadView.Designs) {
      return mobileDesignSelected?.id;
    } else if (designUploadView === DesignUploadView.Uploads) {
      return mobileUploadSelected?.id;
    }
  }

  function addDesign(design: PlaylistObjectView) {
    if (isDesignAdded(design)) return;
    design.sequence = entries.length;
    setEntries([...entries, design]);
  }

  function addUpload(upload: SearchableUpload) {
    if (isUploadAdded(upload)) return;
    const newPlaylistObject = {
      id: '',
      size: '',
      name: upload.name,
      type: DsaPlaylistEntryDtoTypeEnum.Upload,
      thumbnail: getS3Url(upload.path!),
      uploadId: upload.id,
      sequence: entries.length,
    } as PlaylistObjectView;
    setEntries([...entries, newPlaylistObject]);
  }

  function removeEntry(entry: PlaylistObjectView) {
    let currentDesigns = [...entries];
    const thisEntry = entry.type === DsaPlaylistEntryDtoTypeEnum.Upload ? currentDesigns.find(it => it.uploadId === entry.uploadId) : currentDesigns.find(it => it.id === entry.id);
    for (let i = thisEntry?.sequence!; i < currentDesigns.length; i++) {
      currentDesigns.find(it => it.sequence === i)!.sequence = i - 1;
    }
    if (entry.type === DsaPlaylistEntryDtoTypeEnum.Upload) {
      currentDesigns = currentDesigns.filter(it => it.uploadId !== entry.uploadId);
    } else {
      currentDesigns = currentDesigns.filter(it => !(it.id === entry.id && it.page === entry.page));
    }
    setEntries(currentDesigns);
  }

  function onDoneClick() {
    if (saving) return;
    if (entries.length === 0) return;
    setSaving(true);
    setRenaming(false);
    savePlaylist(() => {
      setSaving(false);
      onClose(true);
    })
  }

  function saveBeforePushing() {
    if (screens.length === 0) return;
    if (screens.length === 1) {
      setSaving(true);
      savePlaylist(() => {
        setSaving(false);
      }, true);
    } else {
      setPushPlaylistDropdown(true);
      setSaving(true);
      savePlaylist(() => setSaving(false));
    }
  }

  async function savePlaylist(callback?: any, pushAfterSaving?: boolean) {
    if (currentPlaylist && currentPlaylist.id) {
      try {
        const playlist = await DSAApi.updatePlaylist(currentPlaylist.id, {
          name: name,
          locationId: currentPlaylist.locationId,
          entries: entries.map((it, idx) => {
            let durationSeconds = it.duration || DEFAULT_PLAYLIST_DURATION;
            if (durationSeconds < MIN_PLAYLIST_DURATION) {
              durationSeconds = MIN_PLAYLIST_DURATION;
            }

            return {
              sequence: idx,
              type: it.type,
              designId: it.id,
              page: it.page,
              transition: it.transition || 'fade',
              durationSeconds,
              uploadId: it.type === DsaPlaylistEntryDtoTypeEnum.Upload ? it.uploadId : ''
            } as DsaPlaylistEntryDto;
          })
        });
        setSavedPl(playlist.data);
        if (pushAfterSaving) {
          pushPlaylistToScreens(playlist.data, screens);
        }
        if (callback) {
          callback();
        }
      } catch (e) {
        setSaving(false);
        handleServerError(e);
      }
    } else {
      try {
        const playlist = await DSAApi.createPlaylist({
          name,
          locationId,
          entries: entries.map((it, idx) => {
            let durationSeconds = it.duration || DEFAULT_PLAYLIST_DURATION;
            if (durationSeconds < MIN_PLAYLIST_DURATION) {
              durationSeconds = MIN_PLAYLIST_DURATION;
            }
            return {
              sequence: idx,
              type: it.type,
              designId: it.id,
              page: it.page,
              transition: it.transition || 'fade',
              durationSeconds,
              uploadId: it.uploadId,
            } as DsaPlaylistEntryDto;
          })
        });
        setSavedPl(playlist.data);
        setCurrentPlaylist(playlist.data)
        if (pushAfterSaving) {
          pushPlaylistToScreens(playlist.data, screens);
        }
        if (callback) {
          callback();
        }
      } catch (e) {
        setSaving(false);
        handleServerError(e);
      }
    }
  }

  function hideDropdown() {
    setPushPlaylistDropdown(false);
  }

  const handleClickOutside = (ev: MouseEvent) => {
    hideDropdown();
  }

  useOnClickOutside(menuRef, (ev) => handleClickOutside(ev as MouseEvent));

  function onPushToScreensClick(playlist: DsaPlaylistDto, screens: DsaScreenDto[]) {
    pushPlaylistToScreens(playlist, screens, hideDropdown);
  }

  function changeDuration(e: any, entry: PlaylistObjectView, blur: boolean = false) {
    let value = parseInt(e.target.value);
    if (blur && value < MIN_PLAYLIST_DURATION) {
      value = MIN_PLAYLIST_DURATION;
      message.info(`The minimum transition time is ${MIN_PLAYLIST_DURATION} seconds.`);
    }

    const newEntries = [...entries];
    newEntries.find(it => it === entry)!.duration = value;
    setEntries([...newEntries]);
  }

  function onSortEnd(oldIndex: number, newIndex: number) {
    const currentDesigns = [...entries];
    const thisEntry = currentDesigns.find(it => it.sequence === oldIndex);
    if (newIndex > oldIndex) {
      for (let i = oldIndex; i <= newIndex; i++) {
        currentDesigns.find(it => it.sequence === i)!.sequence = i - 1;
      }
    } else if (oldIndex > newIndex) {
      for (let i = oldIndex; i >= newIndex; i--) {
        currentDesigns.find(it => it.sequence === i)!.sequence = i + 1;
      }
    }
    thisEntry!.sequence = newIndex;
    setEntries(currentDesigns.sort((a, b) => { return a.sequence! - b.sequence! }));
  }

  function onChangeLandscapeFilter() {
    setLandscape(!landscape);
    filterBoards(designs, search, !landscape);

    function showWarning() {
      message.warning('Warning! Landscape and portrait assets will not display well together.');
    }

    if (entries.length > 0) {
      if (!landscape) {
        if (entries.filter((it) => {
          const des = designs.find(des => des.id === it.id && des.page === it.page);
          return isLandscape(des!);
        }).length === 0) {
          showWarning();
        }
      } else {
        if (entries.filter((it) => {
          const des = designs.find(des => des.id === it.id && des.page === it.page);
          return !isLandscape(des!);
        }).length === 0) {
          showWarning();
        }
      }
    }
  }

  function onPublishEntry(designId: string) {
    publish(designId);
    setEntries(prev => prev.map(it => it.id === designId ? { ...it, hasUnpublishedChanges: false } : it));
    setDesigns(prev => prev.map(it => it.id === designId ? { ...it, hasUnpublishedChanges: false } : it));
  }

  function isDesignAdded(des: PlaylistObjectView) {
    return entries.filter(it => it.id === des.id && it.page === des.page).length > 0;
  }

  function isUploadAdded(upload: SearchableUpload) {
    return entries.filter(it => it.uploadId === upload.id).length > 0;
  }

  function isDesignSelected(des: PlaylistObjectView) {
    return mobileDesignSelected.id === des.id && mobileDesignSelected.page === des.page;
  }

  function isUploadSelected(upload: SearchableUpload) {
    return mobileUploadSelected.id === upload.id;
  }

  function startRenaming() {
    setRenaming(true);
    (setRenameInputFocus as () => void)();
  }

  async function rename(ev: any) {
    if (ev.relatedTarget && ev.relatedTarget.classList.contains('done-editing')) return;
    setRenaming(false);
    setSaving(true);
    setTimeout(() => {
      if (!saving) {
        savePlaylist(() => setSaving(false));
      }
    }, 100)
  }

  function onChangeTransition(entry: PlaylistObjectView, transition: string) {
    const clone = [...entries];
    clone.find(it => it.id === entry.id && it.page === entry.page)!.transition = transition;
    setEntries([...clone]);
  }

  function onPreviewClick() {
    if (entries.length === 0) return;
    setPreviewModal(true);
  }

  function withTooltip(tooltip: string, object: ReactNode) {
    return <Tooltip placement="bottom" title={tooltip} getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}>
      {object}
    </Tooltip>
  }

  const content = (<>
    {view === ViewType.Name && <div>
      {mobile && <div className="close-btn" onClick={() => onClose(false)}><CloseOutlined /></div>}
      {mobile && <PlaylistIcon />}
      <h2>Create a Playlist</h2>
      <Input type={"text"} value={name} onChange={ev => setName(ev.target.value)}
        placeholder="Enter Playlist Name" size="large" />

      <footer>
        <Button size={"large"} onClick={() => onClose(false)}>Cancel</Button>
        <Button size={"large"} type={"primary"} disabled={!name} onClick={() => setView(ViewType.Config)}>
          Create
        </Button>
      </footer>
    </div>}

    {view === ViewType.Config && <div>
      {(!mobile || (mobile && mobileEditView === editViews.selectDesign)) &&
        <div className="left">
          <header>
            {mobile && <div className="mobile-title-nav">
              <div className="close-btn back-btn" onClick={() => setMobileEditView(editViews.editPl)}><LeftOutlined /></div>
              <h3>Select a Saved Design</h3>
              <div className="close-btn" onClick={() => onClose(false)}><CloseOutlined /></div>
            </div>}
            {mobile && <div className="location-p"><b>Location:</b> {location.name}</div>}
            <Input type="search" prefix={<SearchOutlined />} placeholder="Search designs"
              value={search} onChange={ev => searchDesignByName(ev.target.value)} />
            <div className="toggle-design-upload-btns">
              {withTooltip("Designs",
              <div onClick={() => onDesignUploadViewClick(DesignUploadView.Designs)} className={`btn ${designUploadView === DesignUploadView.Designs ? 'active' : ''}`}>
                <PaintbrushIcon/>
              </div>)}
              {withTooltip("My Uploads",
              <div onClick={() => onDesignUploadViewClick(DesignUploadView.Uploads)} className={`btn ${designUploadView === DesignUploadView.Uploads ? 'active' : ''}`}>
                <img src="https://timber.mhmcdn.com/site/marketing/icons/Upload.svg" alt="Upload"/>
              </div>)}
            </div>
            {mobile && designUploadView !== DesignUploadView.Uploads && <div className="sw">
                <label>Portrait</label>
                <Switch checked={landscape} onChange={() => onChangeLandscapeFilter()} />
                <label>Landscape</label>
              </div>}
          </header>
          <div className="scroll">
            {(isDesignsLoading || uploading) && <div className="spinner"><Spinner /></div>}
            {!isDesignsLoading && designUploadView === DesignUploadView.Designs &&
            <ul className={"designs"}>
              {search && designs.filter(it => it.visible).length === 0 && <span>
                No matches found.
              </span>}
              {designs
                .filter(it => it.visible)
                .map(it => <li className={`design ${isDesignAdded(it) ? 'added' : ''} ${mobile && isDesignSelected(it) ? 'selected' : ''}`} key={`${it.id}?${it.page}`} onClick={() => onDesignSelect(it)}>
                  <img src={it.thumbnail} alt={it.name} />
                  <h5>{it.name}</h5>
                  <h6>Page {it.page}</h6>
                </li>)}
            </ul>}
            {!isDesignsLoading && designUploadView === DesignUploadView.Uploads &&
            <ul className={"designs"}>
              {search && uploads.filter(it => it.visible).length === 0 && <span>
                No matches found.
              </span>}
              {uploads
                .filter(it => it.visible)
                .map(it => <li className={`design ${isUploadAdded(it) ? 'added' : ''} ${mobile && isUploadSelected(it) ? 'selected' : ''}`} key={`${it.id}`} onClick={() => onUploadSelect(it)}>
                  <MediaThumb src={getS3Url(it.path!)} alt={it.name} />
                  <h5>{it.name}</h5>
                </li>)}
            </ul>}
          </div>
          {mobile && <footer>
            <Button className={`${mobileDoneButtonEnabled() ? '' : 'disabled'}`} size="large" type="primary" onClick={() => onObjectSelectDoneClick()}>Done</Button>
            <div className="help-btn">
              <a href="https://intercom.help/musthavemenus-289b0117ae34/en/articles/11029527-playlists" target="_blank" rel="noreferrer">
                <QuestionCircleOutlined />
                <span>Help</span>
              </a>
            </div>
          </footer>}
        </div>}

      {(!mobile || (mobile && mobileEditView === editViews.editPl)) &&
        <div className="right">
          <header>
            {!isRenaming && (
              <div
                className={`edit-name`}
                onClick={startRenaming}
              >
                <h2>{name}</h2>
                <EditOutlined />
              </div>
            )}
            <div
              className={`editing-name ${isRenaming ? "show" : ""}`}
            >
              <Input
                ref={renameInputRef}
                value={name}
                onChange={(ev) => setName(ev.target.value)}
                onBlur={(ev) => rename(ev)}
              />
            </div>
            <div className="close-btn" onClick={() => onClose(false)}><CloseOutlined /></div>

            <section className="ops">
              {!mobile && designUploadView !== DesignUploadView.Uploads && <div className="sw">
                <label>Portrait</label>
                <Switch checked={landscape} onChange={() => onChangeLandscapeFilter()} />
                <label>Landscape</label>
              </div>}

              <div className="btns">
                {!mobile ?
                  withTooltip("Push to Screen",
                    <Dropdown
                      visible={isPushPlaylistDropdown}
                      destroyPopupOnHide
                      placement="bottomRight"
                      className="transition-dropdown"
                      getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
                      overlayClassName="push-to-screens-dropdown"
                      disabled={screens.length === 0 || entries.length === 0}
                      overlay={
                        <div ref={menuRef}>
                          <PushToScreenMenu
                            playlist={savedPl}
                            onPlaylistPush={onPushToScreensClick}
                            pushLoading={pushLoading || saving}
                            screens={screens}
                          />
                        </div>
                      }
                    >
                      {screens.length === 1 && (pushLoading || saving) ? <LoadingOutlined/>
                      : <Button className={screens.length === 0 || entries.length === 0 ? 'disabled' : ''} type={"link"} onClick={() => saveBeforePushing()}><img src="https://timber.mhmcdn.com/site/marketing/icons/ScreenUpload.svg" alt="push to screen" /></Button>}
                    </Dropdown>
                  )
                  :
                  <>
                    {screens.length === 1 && (pushLoading || saving) ? <LoadingOutlined/>
                    : <Button className={screens.length === 0 || entries.length === 0 ? 'disabled' : ''} type={"link"} onClick={() => saveBeforePushing()}><img src="https://timber.mhmcdn.com/site/marketing/icons/ScreenUpload.svg" alt="push to screen" /></Button>}
                    <Drawer
                      placement={"bottom"}
                      visible={isPushPlaylistDropdown}
                      destroyOnClose
                      className={`mobile-bottom-menu push-to-screens-dropdown`}
                      height={"80vh"}
                      maskClosable={true}
                      onClose={() => setPushPlaylistDropdown(false)}
                      zIndex={1001}
                    >
                      <header>
                        <div className="close-btn back-btn" onClick={() => setPushPlaylistDropdown(false)}><LeftOutlined /></div>
                        <h3>Push to Screens</h3>
                      </header>
                      <PushToScreenMenu
                        playlist={savedPl}
                        onPlaylistPush={onPushToScreensClick}
                        pushLoading={pushLoading || saving}
                        screens={screens}
                      />
                    </Drawer>
                  </>
                }
                {withTooltip("Preview", <Button type={"link"} className={entries.length === 0 ? 'disabled' : ''} onClick={() => onPreviewClick()}>
                  <EyeOutlined />
                </Button>)}
              </div>
            </section>
          </header>

          {mobile && entries.length === 0 && <div className="none-yet">
            <PaintbrushIcon color="#cfcfcf" />
            <div className={`ant-btn secondary`} onClick={() => setMobileEditView(editViews.selectDesign)}>Add a design</div>
          </div>}

          {entries.length === 0 && designUploadView === DesignUploadView.Designs && designs.length === 0 && <div className="none-yet">
            <PaintbrushIcon color="#cfcfcf" />
            <p>You don't have any designs yet.</p>
            <NavLink to={`${env.dashboardPath}/designs`} className={`ant-btn ant-btn-primary`}>Create a design</NavLink>
          </div>}

          {entries.length === 0 && designUploadView === DesignUploadView.Uploads && uploads.length === 0 && <div className="none-yet">
            <img src="https://timber.mhmcdn.com/site/marketing/icons/Upload.svg" alt="Upload"/>
            <p>You don't have any uploads yet.</p>
            <UploadDisplayImage
              beforeUpload={() => setUploading(true)}
              afterUpload={() => {
                setUploading(false);
              }}
              uploads={uploads}
              setUploads={setUploads}
            />
          </div>}

          {(designs.length > 0 || uploads.length > 0) && entries.length > 0 && <div className="entries">
            <SortableList onSortEnd={onSortEnd} className="list" draggedItemClassName="entry-dragging" lockAxis="y">
              {entries.map(it => <SortableItem key={`${it.id}?${it.page}`}>
                <div className={"entry"}>
                  <SortableKnob>
                    <MenuOutlined />
                  </SortableKnob>
                  <MediaThumb
                    src={it.thumbnail}
                    alt={it.name}
                    onClick={() => {
                      if (it.type !== DsaPlaylistEntryDtoTypeEnum.Upload && it.id) {
                        window.open(getDesignTimberLink(it.id), "_blank", "noopener,noreferrer");
                      }
                    }}
                    style={it.type !== DsaPlaylistEntryDtoTypeEnum.Upload ? { cursor: 'pointer', pointerEvents: 'auto' } : undefined}
                  />
                  <div className="entry-info">
                    <div className="entry-title">
                      <h4>{it.name}{it.type !== DsaPlaylistEntryDtoTypeEnum.Upload && <> Page {it.page}</>}</h4>
                      {it.type !== DsaPlaylistEntryDtoTypeEnum.Upload && it.hasUnpublishedChanges &&
                        <UnpublishedChangesNote
                          onPublish={() => onPublishEntry(it.id)}
                        />}
                    </div>
                    <div className="cfg">
                      <Input type="number" size={"small"} min={MIN_PLAYLIST_DURATION} suffix={<ClockCircleOutlined />} placeholder={`${DEFAULT_PLAYLIST_DURATION}s`} value={it.duration} onChange={(e) => changeDuration(e, it)} onBlur={e => changeDuration(e, it, true)} />
                      {withTooltip("Transition",
                        <Dropdown placement="bottomRight" trigger={['click']} className="transition-dropdown" getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
                          overlay={<Radio.Group onChange={(e) => onChangeTransition(it, e.target.value)} value={it.transition || 'fade'}>
                            <Radio value="fade">Fade</Radio>
                            <Radio value="slide-left">Slide Left</Radio>
                            <Radio value="slide-right">Slide Right</Radio>
                            <Radio value="slide-up">Slide Up</Radio>
                            <Radio value="slide-down">Slide Down</Radio>
                            <Radio value="card-flip-vertical">Card Flip Vertical</Radio>
                            <Radio value="card-flip-horizontal">Card Flip Horizontal</Radio>
                            <Radio value="none">None</Radio>
                          </Radio.Group>}>
                          <Button><TransitionIcon /></Button>
                        </Dropdown>)}
                      {withTooltip("Delete",
                        <Button onClick={() => removeEntry(it)}><DeleteOutlined /></Button>)}
                    </div>
                  </div>
                </div>
              </SortableItem>)}
            </SortableList>
            {mobile && entries.length > 0 &&
              <div className="mobile-add-des">
                <div className={`ant-btn secondary`} onClick={() => setMobileEditView(editViews.selectDesign)}>Add a design</div>
              </div>}
          </div>}

          <footer>
            <Button className={`done-editing ${entries.length === 0 ? 'disabled' : ''}`} size="large" type="primary" onMouseDown={() => onDoneClick()} loading={saving}>Done</Button>
            {!mobile && withTooltip("Help", <a className="help-btn" href="https://intercom.help/musthavemenus-289b0117ae34/en/articles/11029527-playlists" target="_blank" rel="noreferrer"><QuestionCircleOutlined /></a>)}
            {mobile && <div className="help-btn">
              <a href="https://intercom.help/musthavemenus-289b0117ae34/en/articles/11029527-playlists" target="_blank" rel="noreferrer">
                <QuestionCircleOutlined />
                <span>Help</span>
              </a>
            </div>}
          </footer>
        </div>}
    </div>}
  </>)

  return (
    !mobile ?
      <Modal
        className={`clean kit-plt ${view === ViewType.Name ? "nm" : 'cf'}`}
        visible={isVisible}
        onCancel={() => onClose(false)}
        transitionName=""
      >
        {content}
      </Modal>
      :
      <Drawer
        placement={"bottom"}
        visible={isVisible}
        className={`mobile-bottom-menu kit-plt ${view === ViewType.Name ? "nm" : 'cf'}`}
        height={"80vh"}
        closeIcon={false}
        maskClosable={true}
        push={false}
        onClose={() => onClose(false)}
      >
        {content}
      </Drawer>
  );
}
